import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDeCompetencia } from '@/componentes/CampoDeData';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { Janela } from '@/componentes/JanelaDeAcao';
import { formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDaRenovacao, type Risco, type TipoDeProposta } from './api';
import { codigoDaProposta, estudoMaisRecente, NOME_DO_RISCO, NOME_DO_TIPO_DE_PROPOSTA } from './renovacao';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

export interface InicioDaProposta {
  readonly tipo: TipoDeProposta;
  readonly equipamentoId: string | null;
  readonly estudoId: string | null;
  readonly descricao: string;
  readonly investimento: number | null;
}

const ADICIONAL: readonly TipoDeProposta[] = ['comprar_adicional', 'alugar_adicional'];

function mesQueVem(): string {
  const d = new Date();
  const proximo = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return `${String(proximo.getFullYear())}-${String(proximo.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * PROPOR O INVESTIMENTO (ferramenta 4): o tipo, a máquina (ou o tipo de
 * máquina, no adicional), o valor, a data pretendida e o risco operacional
 * com a justificativa. Quem propõe não aprova: a decisão é de outra pessoa.
 * Do estudo concluído, a janela já vem com o tipo e o valor das premissas.
 * São mais de seis campos, mas a janela é a do formulário da proposta (p.30);
 * dúvida de design registrada.
 */
export function JanelaDaProposta({ aberta, aoFechar, inicio = null }: { readonly aberta: boolean; readonly aoFechar: () => void; readonly inicio?: InicioDaProposta | null }) {
  const cliente = useQueryClient();
  const opcoes = useQuery({ queryKey: ['renovacao', 'opcoes'], queryFn: apiDaRenovacao.opcoes, enabled: aberta, meta: { oQueLia: 'as máquinas e os estudos' } });
  const comVisualDaFrota = useOpcoesComVisualDaFrota(aberta);
  const [tipo, setTipo] = useState<TipoDeProposta>(inicio?.tipo ?? 'substituir_comprando');
  const [maquina, setMaquina] = useState(inicio?.equipamentoId ?? '');
  const [tipoDeMaquina, setTipoDeMaquina] = useState('');
  const [estudo, setEstudo] = useState(inicio?.estudoId ?? '');
  const [descricao, setDescricao] = useState(inicio?.descricao ?? '');
  const [investimento, setInvestimento] = useState(inicio?.investimento ? String(inicio.investimento).replace('.', ',') : '');
  const [mensal, setMensal] = useState('');
  const [meses, setMeses] = useState('');
  const [data, setData] = useState(mesQueVem);
  const [risco, setRisco] = useState<Risco>('medio');
  const [justificativa, setJustificativa] = useState('');
  const [tentou, setTentou] = useState(false);
  const adicional = ADICIONAL.includes(tipo);
  const valorInvestido = investimento.trim() ? interpretarNumero(investimento) : null;
  const valorMensal = mensal.trim() ? interpretarNumero(mensal) : null;
  const numeroDeMeses = meses.trim() ? Number(meses) : null;
  const falta = {
    maquina: !adicional && !maquina ? 'Escolha a máquina da proposta.' : null,
    tipoDeMaquina: adicional && !tipoDeMaquina ? 'Escolha o tipo de máquina que falta.' : null,
    descricao: descricao.trim().length < 10 ? 'Descreva a proposta com pelo menos 10 caracteres, por exemplo "escavadeira 20 t nova para a obra norte".' : null,
    valor: tipo !== 'vender_sem_repor' && valorInvestido === null && valorMensal === null ? 'Informe o investimento ou a locação mensal.' : null,
    meses: (valorMensal === null) !== (numeroDeMeses === null || !Number.isInteger(numeroDeMeses)) ? 'Informe a locação mensal e os meses do compromisso juntos.' : null,
    data: /^\d{4}-(0[1-9]|1[0-2])$/.test(data) ? null : 'Escolha o mês pretendido.',
    justificativa: justificativa.trim().length < 10 ? 'Explique o risco com pelo menos 10 caracteres.' : null,
  };
  const valido = Object.values(falta).every((x) => x === null);
  const propor = useMutation({
    mutationFn: () => apiDaRenovacao.propor({
      tipo,
      equipamentoId: adicional ? null : maquina,
      tipoDeMaquina: adicional ? tipoDeMaquina : null,
      estudoId: adicional || !estudo ? null : estudo,
      descricao,
      investimento: valorInvestido,
      compromissoMensal: valorMensal,
      mesesDeCompromisso: valorMensal === null ? null : numeroDeMeses,
      dataPretendida: data,
      risco,
      justificativaDoRisco: justificativa,
    }),
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.propor>>>((r) => ({
        titulo: 'Investimento proposto',
        alvo: { codigo: codigoDaProposta(r.numero), nome: r.maquina ?? r.tipoDeMaquina ?? r.tipo },
        fatos: [r.tipo, r.investimento === null ? null : formatarReais(r.investimento), r.compromissoMensal === null ? null : `${formatarReais(r.compromissoMensal)}/mês`],
        efeito: 'A decisão é de outra pessoa com a licença de aprovar investimentos',
        ver: '/investimentos?aba=propostas',
      })),
    },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
    },
  });
  const estudosDaMaquina = (opcoes.data?.estudosConcluidos ?? []).filter((e) => e.equipamentoId === maquina);
  return (
    <Janela
      titulo="Propor investimento"
      descricao="Quem propõe não aprova: a decisão é de outra pessoa com a licença de aprovar investimentos."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="g"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" carregando={propor.isPending} onClick={() => { setTentou(true); if (valido) propor.mutate(); }}>Propor investimento</Botao>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {propor.isError ? <p role="alert" className="txt-apoio text-parada-texto sm:col-span-2">{`${explicarFalha(propor.error).erro}. ${explicarFalha(propor.error).comoResolver}`}</p> : null}
        <CampoSelecao rotulo="Tipo da proposta" valor={tipo} aoMudar={(v) => { setTipo(v as TipoDeProposta); }} opcoes={(Object.keys(NOME_DO_TIPO_DE_PROPOSTA) as TipoDeProposta[]).map((t) => ({ valor: t, rotulo: NOME_DO_TIPO_DE_PROPOSTA[t] }))} />
        {opcoes.isPending ? <ControleCarregando oQue="Carregando as máquinas" /> : adicional ? (
          <div className="flex flex-col gap-1">
            <CampoSelecao rotulo="Tipo de máquina" valor={tipoDeMaquina} aoMudar={setTipoDeMaquina} rotuloVazio="Escolha o tipo" opcoes={(opcoes.data?.tipos ?? []).map((t) => ({ valor: t, rotulo: t }))} />
            {tentou && falta.tipoDeMaquina ? <p className="txt-apoio text-parada-texto">{falta.tipoDeMaquina}</p> : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {/* A máquina traz o estudo concluído mais recente dela (`estudoMaisRecente`). */}
            <CampoSelecao rotulo="Máquina" valor={maquina} aoMudar={(v) => { setMaquina(v); setEstudo(estudoMaisRecente(opcoes.data?.estudosConcluidos ?? [], v)); }} rotuloVazio="Escolha a máquina" opcoes={comVisualDaFrota((opcoes.data?.maquinas ?? []).map((m) => opcaoDaMaquina(m)))} adesivo />
            {tentou && falta.maquina ? <p className="txt-apoio text-parada-texto">{falta.maquina}</p> : null}
          </div>
        )}
        {!adicional && estudosDaMaquina.length > 0 ? (
          <CampoSelecao rotulo="Estudo concluído" opcional valor={estudo} aoMudar={setEstudo} {...(estudo !== '' && estudo === estudoMaisRecente(estudosDaMaquina, maquina) && estudosDaMaquina.length > 1 ? { ajuda: 'O mais recente desta máquina.' } : {})} rotuloVazio="Sem estudo" opcoes={estudosDaMaquina.map((e) => ({ valor: e.id, rotulo: `EST-${String(e.numero).padStart(4, '0')} · ${e.alternativa}` }))} />
        ) : null}
        <div className="sm:col-span-2">
          <CampoTexto rotulo="Descrição" valor={descricao} aoMudar={setDescricao} placeholder="Escavadeira 20 t nova para a obra norte" validar={() => (tentou ? falta.descricao : null)} maxLength={300} />
        </div>
        <CampoTexto rotulo="Investimento (R$)" opcional valor={investimento} aoMudar={setInvestimento} placeholder="1.150.000,00" validar={() => (tentou ? falta.valor : null)} maxLength={20} />
        {/* O mês escolhido por nome ("Novembro de 2026"), e não digitado como aaaa-mm (achado dos robôs, 20/10/2026). */}
        <CampoDeCompetencia rotulo="Mês pretendido" valor={`${data}-01`} aoMudar={(v) => { setData(v.slice(0, 7)); }} mesesAFrente={36} mesesAtras={0} />
        <CampoTexto rotulo="Locação mensal (R$)" opcional valor={mensal} aoMudar={setMensal} placeholder="42.000,00" maxLength={20} />
        <CampoTexto rotulo="Meses do compromisso" opcional valor={meses} aoMudar={setMeses} placeholder="24" validar={() => (tentou ? falta.meses : null)} maxLength={3} />
        <CampoSelecao rotulo="Risco operacional" valor={risco} aoMudar={(v) => { setRisco(v as Risco); }} opcoes={(Object.keys(NOME_DO_RISCO) as Risco[]).map((r) => ({ valor: r, rotulo: NOME_DO_RISCO[r] }))} />
        <div className="sm:col-span-2">
          <CampoDoMotivo rotulo="Por que este risco" valor={justificativa} aoMudar={setJustificativa} exemplo="Sem a máquina, a frente de corte da obra norte para" minimo={10} maximo={300} />
        </div>
      </div>
    </Janela>
  );
}
